import type { ReactNode } from 'react';
import { useLanguage } from '@/contexts/AppContext';
import type { Language, Locale } from '@/config/locales';

interface TableOrderHeaderProps {
  onWaiterClick: () => void;
  canCallWaiter?: boolean;
  onPaymentClick: () => void;
  canPay: boolean;
  storeName?: string;
  tableNumber?: string | null;
  sessionControl?: ReactNode;
}

/** 헤더 라벨 문자열 */
interface TableHeaderLabels {
  waiter: string;
  pay: string;
  mesa: string;
}

// #522 — Locale 단일 출처 정합 (15언어 전수, 키 누락 시 컴파일 에러).
const TABLE_HEADER_LABELS:
  & Record<Language, TableHeaderLabels>
  & Record<Exclude<Locale, Language>, TableHeaderLabels> = {
  ko: { waiter: '직원호출', pay: '결제', mesa: '테이블' },
  es: { waiter: 'MESERO', pay: 'PAGAR', mesa: 'Mesa' },
  en: { waiter: 'WAITER', pay: 'PAY', mesa: 'Table' },
  pt: { waiter: 'GARÇOM', pay: 'PAGAR', mesa: 'Mesa' },
  fr: { waiter: 'SERVEUR', pay: 'PAYER', mesa: 'Table' },
  zh: { waiter: '服务员', pay: '结账', mesa: '餐桌' },
  ja: { waiter: '店員呼出', pay: '会計', mesa: 'テーブル' },
  hi: { waiter: 'वेटर', pay: 'भुगतान', mesa: 'टेबल' },
  de: { waiter: 'KELLNER', pay: 'ZAHLEN', mesa: 'Tisch' },
  it: { waiter: 'CAMERIERE', pay: 'PAGARE', mesa: 'Tavolo' },
  ru: { waiter: 'ОФИЦИАНТ', pay: 'ОПЛАТА', mesa: 'Стол' },
  ar: { waiter: 'النادل', pay: 'الدفع', mesa: 'طاولة' },
  vi: { waiter: 'GỌI NHÂN VIÊN', pay: 'THANH TOÁN', mesa: 'Bàn' },
  th: { waiter: 'เรียกพนักงาน', pay: 'ชำระเงิน', mesa: 'โต๊ะ' },
  nl: { waiter: 'OBER', pay: 'BETALEN', mesa: 'Tafel' },
};

export function TableOrderHeader({
  onWaiterClick,
  canCallWaiter = true,
  onPaymentClick,
  canPay,
  storeName,
  tableNumber,
  sessionControl,
}: TableOrderHeaderProps) {
  // #522: 15 locale 전수 — language(3종) 대신 locale 로 직접 조회
  const { locale } = useLanguage();
  const t = TABLE_HEADER_LABELS[locale] ?? TABLE_HEADER_LABELS.es;
  return (
    <div data-testid="table-order-header" className="flex flex-col items-center justify-center gap-2">
      {/* 매장명 + 테이블 번호 — 테이블 세션 컨텍스트 시각화 (메뉴 화면이 픽업과 동일하게 보이는 문제 해결) */}
      {(storeName || tableNumber) && (
        <div className="flex flex-col items-center justify-center w-full pt-2">
          {storeName && (
            <h2 className="text-foreground text-[clamp(16px,4.5vw,20px)] font-semibold leading-tight">
              {storeName}
            </h2>
          )}
          {tableNumber && (
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-0.5 text-primary text-[clamp(12px,3.4vw,14px)] font-medium">
              <span aria-hidden>🪑</span>
              <span>{t.mesa} {tableNumber}</span>
            </div>
          )}
        </div>
      )}
      {sessionControl && (
        <div className="w-full max-w-md px-2">
          {sessionControl}
        </div>
      )}
      <div className="box-border content-stretch flex gap-[30px] items-center justify-center px-[10px] py-[20px] relative w-full">
        {/* 웨이터 버튼 */}
        <button
          type="button"
          onClick={onWaiterClick}
          disabled={!canCallWaiter}
          className="inline-flex w-[78px] shrink-0 flex-col items-center justify-center gap-1 text-center transition-transform duration-200 enabled:active:scale-95 enabled:hover:scale-105 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <div className="relative flex size-[70px] items-center justify-center rounded-full bg-muted text-primary">
            <div className="h-[34.753px] w-[37.692px]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 38 35">
                <g clipPath="url(#clip0_71_532)">
                  <path d="M22.8174 30.0866C22.8174 32.1731 21.1347 33.8558 19.0481 33.8558C16.9616 33.8558 15.2789 32.1731 15.2789 30.0866" stroke="currentColor" strokeMiterlimit="10" strokeWidth="1.66667" />
                  <path d="M5.49692 9.44534H0.897559" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                  <path d="M5.49692 9.44534H0.897559" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                  <path d="M32.1954 9.44534H36.7947" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                  <path d="M10.0287 4.06085L5.27229 0.897386" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                  <path d="M31.7466 25.7788L30.1312 23.2435C29.7498 22.6377 29.5479 21.9422 29.5254 21.2243L29.3908 17.7018C29.3908 12.7659 26.0479 8.6153 21.5158 7.35889V6.12491C21.5158 4.53197 20.237 3.25312 18.644 3.25312C17.0511 3.25312 15.7722 4.53197 15.7722 6.12491V7.35889C11.2402 8.6153 7.89724 12.7659 7.89724 17.7018L7.78506 21.2243C7.78506 21.9422 7.5607 22.6377 7.17929 23.2435L5.58634 25.7563C4.64403 27.3044 5.76583 29.2788 7.5607 29.2788H29.7722C31.5895 29.2788 32.6889 27.3044 31.7466 25.7563V25.7788Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                  <path d="M9.28832 3.56726L5.27229 0.897386" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                  <path d="M27.6633 4.06085L32.4197 0.897386" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" />
                </g>
                <defs>
                  <clipPath id="clip0_71_532">
                    <rect fill="white" height="34.7532" width="37.6923" />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>
          <p className="max-w-full break-words text-center font-['Noto_Sans:Medium',_sans-serif] text-[10px] font-medium leading-tight text-primary" style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
            {t.waiter}
          </p>
        </button>

        {/* 구분선 */}
        <div className="h-[37.5px] relative shrink-0 w-0">
          <div className="absolute bottom-0 left-[-0.5px] right-[-0.5px] top-0 text-primary">
            <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 2 38">
              <path d="M1 0V37.5" stroke="currentColor" />
            </svg>
          </div>
        </div>

        {/* 결제 버튼 */}
        <button
          type="button"
          onClick={onPaymentClick}
          disabled={!canPay}
          className={`inline-flex w-[78px] shrink-0 flex-col items-center justify-center gap-1 text-center transition-all duration-200 ${
            !canPay
              ? 'opacity-40 cursor-not-allowed'
              : 'active:scale-95 hover:scale-105 cursor-pointer'
          }`}
        >
          <div className="relative flex size-[70px] items-center justify-center rounded-full bg-muted text-primary">
            <div className="h-[25.846px] w-[38.26px]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 39 26">
                <g clipPath="url(#clip0_71_526)">
                  <path d="M33.5118 1.809C35.1399 1.809 36.4514 3.12053 36.4514 4.74864V21.0975C36.4514 22.7256 35.1399 24.0371 33.5118 24.0371H4.74864C3.12053 24.0371 1.809 22.7256 1.809 21.0975V4.74864C1.809 3.12053 3.12053 1.809 4.74864 1.809H33.5118ZM33.5118 0H4.74864C2.12558 0 0 2.12558 0 4.74864V21.0975C0 23.7206 2.12558 25.8462 4.74864 25.8462H33.5118C36.1349 25.8462 38.2604 23.7206 38.2604 21.0975V4.74864C38.2604 2.12558 36.1349 0 33.5118 0Z" fill="currentColor" />
                  <path d="M10.1758 15.3088H6.15078C5.62626 15.3088 5.20106 15.734 5.20106 16.2585V18.8363C5.20106 19.3609 5.62626 19.7861 6.15078 19.7861H10.1758C10.7003 19.7861 11.1255 19.3609 11.1255 18.8363V16.2585C11.1255 15.734 10.7003 15.3088 10.1758 15.3088Z" fill="currentColor" />
                  <path d="M1.58283 6.10532H36.7454" stroke="currentColor" strokeMiterlimit="10" strokeWidth="1.67979" />
                  <path d="M1.58283 10.7863H36.7454" stroke="currentColor" strokeMiterlimit="10" strokeWidth="1.67979" />
                </g>
                <defs>
                  <clipPath id="clip0_71_526">
                    <rect fill="white" height="25.8462" width="38.2604" />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>
          <p className="max-w-full break-words text-center font-['Noto_Sans:Medium',_sans-serif] text-[10px] font-medium leading-tight text-primary" style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
            {t.pay}
          </p>
        </button>
      </div>
    </div>
  );
}
